import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useMessages } from "@gss/components/Messages";
import { Table } from "@gss/components/Table";
import { useParams } from "@gss/hooks/useParams";
import type { MeasurementStat } from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type {
    DimensionScore,
    QuestionScore,
    QuestionScoreQuestion,
} from "@gss/types/statScore";
import { DAO } from "../DAO";

interface AugmentedStatQuestionQuestion {
    question: QuestionScoreQuestion;
    dimension: string;
}

export function Summary(): JSX.Element {
    const [dimensionScore, setDimensionScore] = useState<DimensionScore>();
    const [questions, setQuestions] =
        useState<AugmentedStatQuestionQuestion[]>();
    const params = useParams();
    const measurementName = params.get("name");
    const periods = params.get("period");
    const orgStructure = "internal";
    const audience: SecurityContext = "authenticated";
    const { addError } = useMessages();

    useEffect(() => {
        DAO.stats
            .getMeasurementStat(audience, measurementName, periods)
            .then((stat) => {
                // oxlint-disable-next-line react/immutability
                loadData(stat);
            })
            .catch(addError);
    }, []);

    const loadData = (stat: MeasurementStat) => {
        DAO.stats
            .getDimensionScore(audience, measurementName, orgStructure, periods)
            .then(setDimensionScore)
            .catch(addError);

        DAO.stats
            .getQuestionScore(audience, measurementName, orgStructure, periods)
            .then((questionScore) => {
                setQuestions(getQuestions(stat, questionScore));
            })
            .catch(addError);
    };

    const renderDimensions = () => {
        if (dimensionScore == null || dimensionScore.status === "nok") {
            return null;
        }
        return (
            <div>
                <Table
                    striped
                    headers={
                        <tr>
                            <th>Dimension</th>
                            <th>Poäng</th>
                        </tr>
                    }
                >
                    {dimensionScore.dimensions.map((dimension) => {
                        return (
                            <tr key={dimension.name}>
                                <td>{dimension.name}</td>
                                <td>{dimension.score}</td>
                            </tr>
                        );
                    })}
                </Table>
            </div>
        );
    };

    const renderQuestions = () => {
        if (questions == null) {
            return null;
        }
        return (
            <div>
                <Table
                    striped
                    headers={
                        <tr>
                            <th>Dimension</th>
                            <th>Fråge-ID</th>
                            <th>Medelvärde (%)</th>
                        </tr>
                    }
                >
                    {questions.map((q) => (
                        <tr key={q.question.id}>
                            <td>{q.dimension}</td>
                            <td>{q.question.id}</td>
                            <td>
                                {q.question.status === "ok"
                                    ? q.question.score.value
                                    : null}
                            </td>
                        </tr>
                    ))}
                </Table>
            </div>
        );
    };

    return (
        <div>
            <h2>Visar statistik på rikesnivå</h2>
            <div>Mätning: {measurementName}</div>
            <div>Perioder: {periods}</div>
            <div>Orgstruktur: {orgStructure}</div>
            &nbsp;
            <h3>Dimenspoäng</h3>
            {renderDimensions()}
            <h3>Frågor</h3>
            {renderQuestions()}
        </div>
    );
}

function getQuestions(
    stat: MeasurementStat,
    questionScore: QuestionScore,
): AugmentedStatQuestionQuestion[] {
    return stat.questions
        .filter((q) => !q.choiceFreeText)
        .map((question) => {
            const found = questionScore.questions.find(
                (q) => q.id === question.id,
            );
            if (found == null) {
                throw new Error(`Question '${question.id}' not found`);
            }
            return { question: found, dimension: question.dimension };
        });
}
